import { useState } from "react";
import { Container, StarsRating, Button, Skeleton } from "@/components/atoms";
import {
  ReviewCard,
  FilterPillRow,
  EmptyState,
  type FilterOption,
} from "@/components/molecules";
import { useReviews } from "@/hooks/use-reviews";
import type { Product } from "@/types";

const filters: FilterOption[] = [
  { value: "all", label: "All" },
  { value: "5", label: "5 ★" },
  { value: "4", label: "4 ★" },
  { value: "verified", label: "Verified" },
];

export function ReviewsSection({ product }: { product: Product }) {
  const { data: reviews, isLoading } = useReviews(product.id);
  const [filter, setFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const filtered = (reviews ?? []).filter((r) => {
    if (filter === "all") return true;
    if (filter === "verified") return r.verified;
    return String(r.rating) === filter;
  });
  const visible = showAll ? filtered : filtered.slice(0, 3);

  return (
    <section className="bg-medical-grey py-12">
      <Container>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-condensed text-2xl font-bold text-night-blue">
              Patient reviews
            </h2>
            <div className="mt-1 flex items-center gap-2">
              <StarsRating value={product.ratingsAverage} size={18} />
              <span className="text-sm text-night-blue/70">
                {product.ratingsAverage.toFixed(1)} · {product.ratingsCount}{" "}
                reviews
              </span>
            </div>
          </div>
          <FilterPillRow
            options={filters}
            active={filter}
            onChange={setFilter}
          />
        </div>

        {isLoading ? (
          /* CLS — a 40vh spinner standing in for a list of review cards is a guaranteed shift in
             both directions. Three card-shaped blocks match the default `visible` slice. */
          <div className="space-y-3 rounded-card bg-white p-6">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <EmptyState
            title="No reviews yet"
            description="Be the first to share your experience."
          />
        ) : (
          <div className="rounded-card bg-white px-6">
            {visible.map((r) => (
              <ReviewCard key={r.id} review={r} />
            ))}
          </div>
        )}

        {!showAll && filtered.length > 3 && (
          <div className="mt-6 text-center">
            <Button variant="outline" onClick={() => setShowAll(true)}>
              See all {filtered.length} reviews
            </Button>
          </div>
        )}
      </Container>
    </section>
  );
}
